// System General Settings Constants
export const TIMEZONE_OPTIONS = [
  { value: 'UTC', label: 'UTC (Coordinated Universal Time)' },
  { value: 'America/New_York', label: 'Eastern Time (ET)' },
  { value: 'America/Chicago', label: 'Central Time (CT)' },
  { value: 'America/Denver', label: 'Mountain Time (MT)' },
  { value: 'America/Los_Angeles', label: 'Pacific Time (PT)' },
  { value: 'Europe/London', label: 'Greenwich Mean Time (GMT)' },
  { value: 'Europe/Berlin', label: 'Central European Time (CET)' },
  { value: 'Asia/Tokyo', label: 'Japan Standard Time (JST)' },
  { value: 'Australia/Sydney', label: 'Australian Eastern Time (AET)' },
] as const;

// Aliases for compatibility
export const TIME_ZONES = TIMEZONE_OPTIONS;

export const LANGUAGE_OPTIONS = [
  { value: 'en', label: 'English', flag: '🇺🇸' },
  { value: 'es', label: 'Español', flag: '🇪🇸' },
  { value: 'fr', label: 'Français', flag: '🇫🇷' },
  { value: 'de', label: 'Deutsch', flag: '🇩🇪' },
  { value: 'pt', label: 'Português', flag: '🇵🇹' },
  { value: 'ja', label: '日本語', flag: '🇯🇵' },
] as const;

// Aliases for compatibility
export const LANGUAGES = LANGUAGE_OPTIONS;

// Currency options
export const CURRENCIES = [
  { value: 'USD', label: 'US Dollar ($)', symbol: '$' },
  { value: 'EUR', label: 'Euro (€)', symbol: '€' },
  { value: 'GBP', label: 'British Pound (£)', symbol: '£' },
  { value: 'CAD', label: 'Canadian Dollar (C$)', symbol: 'C$' },
  { value: 'AUD', label: 'Australian Dollar (A$)', symbol: 'A$' },
  { value: 'JPY', label: 'Japanese Yen (¥)', symbol: '¥' },
  { value: 'CHF', label: 'Swiss Franc (CHF)', symbol: 'CHF' },
  { value: 'CNY', label: 'Chinese Yuan (¥)', symbol: '¥' },
] as const;

export const DATE_FORMATS = [
  { value: 'MM/DD/YYYY', label: 'MM/DD/YYYY (US)' },
  { value: 'DD/MM/YYYY', label: 'DD/MM/YYYY (EU)' },
  { value: 'YYYY-MM-DD', label: 'YYYY-MM-DD (ISO)' },
  { value: 'DD MMM YYYY', label: 'DD MMM YYYY' },
] as const;

export const TIME_FORMATS = [
  { value: '12', label: '12-hour (AM/PM)' },
  { value: '24', label: '24-hour' },
] as const;

// Branding Constants
export const BRAND_COLORS = [
  { name: 'Primary', key: 'primary', default: '#1890ff' },
  { name: 'Secondary', key: 'secondary', default: '#722ed1' },
  { name: 'Success', key: 'success', default: '#52c41a' },
  { name: 'Warning', key: 'warning', default: '#faad14' },
  { name: 'Error', key: 'error', default: '#f5222d' },
  { name: 'Background', key: 'background', default: '#f0f2f5' },
  { name: 'Surface', key: 'surface', default: '#ffffff' },
  { name: 'Text Primary', key: 'textPrimary', default: '#262626' },
  { name: 'Text Secondary', key: 'textSecondary', default: '#8c8c8c' },
] as const;

export const LOGO_SIZES = [
  { value: 'small', label: 'Small (120x40px)', width: 120, height: 40 },
  { value: 'medium', label: 'Medium (180x60px)', width: 180, height: 60 },
  { value: 'large', label: 'Large (240x80px)', width: 240, height: 80 },
] as const;

// Notification Constants
export const NOTIFICATION_TYPES = [
  { value: 'email', label: 'Email', icon: 'MailOutlined' },
  { value: 'sms', label: 'SMS', icon: 'MessageOutlined' },
  { value: 'push', label: 'Push Notification', icon: 'NotificationOutlined' },
  { value: 'webhook', label: 'Webhook', icon: 'ApiOutlined' },
] as const;

export const NOTIFICATION_EVENTS = [
  { value: 'user_signup', label: 'User Signup', category: 'user' },
  { value: 'user_login', label: 'User Login', category: 'user' },
  { value: 'password_reset', label: 'Password Reset', category: 'security' },
  { value: 'program_assigned', label: 'Program Assigned', category: 'program' },
  { value: 'assessment_due', label: 'Assessment Due', category: 'assessment' },
  { value: 'payment_received', label: 'Payment Received', category: 'billing' },
  { value: 'subscription_expiring', label: 'Subscription Expiring', category: 'billing' },
  { value: 'system_maintenance', label: 'System Maintenance', category: 'system' },
] as const;

// Security Constants
export const PASSWORD_POLICIES = [
  { value: 'length', label: 'Minimum Length', type: 'number', default: 8, min: 6, max: 32 },
  { value: 'uppercase', label: 'Require Uppercase', type: 'boolean', default: true },
  { value: 'lowercase', label: 'Require Lowercase', type: 'boolean', default: true },
  { value: 'numbers', label: 'Require Numbers', type: 'boolean', default: true },
  { value: 'symbols', label: 'Require Symbols', type: 'boolean', default: false },
  { value: 'expiry', label: 'Password Expiry (days)', type: 'number', default: 90, min: 0, max: 365 },
] as const;

export const SESSION_TIMEOUTS = [
  { value: 15, label: '15 minutes' },
  { value: 30, label: '30 minutes' },
  { value: 60, label: '1 hour' },
  { value: 240, label: '4 hours' },
  { value: 480, label: '8 hours' },
  { value: 1440, label: '24 hours' },
  { value: 0, label: 'Never expire' },
] as const;

// Data Retention Constants
export const RETENTION_PERIODS = [
  { value: 30, label: '30 days' },
  { value: 90, label: '3 months' },
  { value: 180, label: '6 months' },
  { value: 365, label: '1 year' },
  { value: 1095, label: '3 years' },
  { value: 2555, label: '7 years' },
  { value: -1, label: 'Indefinite' },
] as const;

export const DATA_CATEGORIES = [
  { value: 'user_activity', label: 'User Activity Logs', default: 365 },
  { value: 'system_logs', label: 'System Logs', default: 90 },
  { value: 'audit_logs', label: 'Audit Logs', default: 2555 },
  { value: 'session_data', label: 'Session Data', default: 30 },
  { value: 'temp_files', label: 'Temporary Files', default: 7 },
  { value: 'backup_files', label: 'Backup Files', default: 1095 },
] as const;

// Integration Constants
export const INTEGRATION_TYPES = [
  { value: 'oauth', label: 'OAuth 2.0', icon: 'SafetyCertificateOutlined' },
  { value: 'api_key', label: 'API Key', icon: 'KeyOutlined' },
  { value: 'webhook', label: 'Webhook', icon: 'ApiOutlined' },
  { value: 'sso', label: 'Single Sign-On (SSO)', icon: 'LoginOutlined' },
] as const;

export const POPULAR_INTEGRATIONS = [
  { name: 'Google Workspace', type: 'oauth', category: 'productivity' },
  { name: 'Microsoft 365', type: 'oauth', category: 'productivity' },
  { name: 'Slack', type: 'webhook', category: 'communication' },
  { name: 'Stripe', type: 'api_key', category: 'payment' },
  { name: 'PayPal', type: 'oauth', category: 'payment' },
  { name: 'Mailchimp', type: 'api_key', category: 'marketing' },
  { name: 'Salesforce', type: 'oauth', category: 'crm' },
  { name: 'Zoom', type: 'oauth', category: 'communication' },
] as const;

// Backup Constants
export const BACKUP_FREQUENCIES = [
  { value: 'hourly', label: 'Every Hour' },
  { value: 'daily', label: 'Daily' },
  { value: 'weekly', label: 'Weekly' },
  { value: 'monthly', label: 'Monthly' },
] as const;

export const BACKUP_TYPES = [
  { value: 'full', label: 'Full Backup', description: 'Complete system backup' },
  { value: 'incremental', label: 'Incremental', description: 'Changes since last backup' },
  { value: 'differential', label: 'Differential', description: 'Changes since last full backup' },
] as const;

export const BACKUP_DESTINATIONS = [
  { value: 'local', label: 'Local Storage', icon: 'HddOutlined' },
  { value: 'aws_s3', label: 'Amazon S3', icon: 'CloudOutlined' },
  { value: 'google_drive', label: 'Google Drive', icon: 'GoogleOutlined' },
  { value: 'azure_blob', label: 'Azure Blob Storage', icon: 'CloudOutlined' },
] as const; 